import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuth } from '../../../context/AuthContext';
import { bookingsAPI, locationsAPI, getServerTime } from '../../../api/bookings';
import { bruneiToday, bruneiDateOffset } from '../../../lib/brunei';

import {
  buildTimeSlots,
  getConsecutiveSlots as computeConsecutiveSlots,
  isTimeSlotPast as computeIsTimeSlotPast,
  getOperatingHoursForDate,
  formatOperatingHoursDisplay,
} from './utils';

// All BookingFlow state, effects and handlers. Returns a single flat object
// so the shell can destructure without thinking.
export function useBookingFlow() {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const { user, isGuest } = useAuth();

  // ---- step + loading flags ----
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [showWaitlistDialog, setShowWaitlistDialog] = useState(false);

  // ---- location / date / time / duration ----
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [duration, setDuration] = useState(1);
  const [availability, setAvailability] = useState(null);
  const [slotPricing, setSlotPricing] = useState({});

  // ---- courts requested + pricing ----
  const [courtsRequested, setCourtsRequested] = useState([
    { sport: 'badminton', quantity: 1, _uid: `court-${Date.now()}` },
  ]);
  const [courtsPricing, setCourtsPricing] = useState([]);

  // Per-sport hourly rate for the currently-selected slot/duration.
  // Populated so each sport pill can display its own price even when the
  // sport is not the currently-selected one. Shape: { badminton: 14, pickleball: 14 }
  const [sportPriceMap, setSportPriceMap] = useState({});

  // ---- misc form fields ----
  const [specialRequests, setSpecialRequests] = useState('');

  // ---- server time bounds (Brunei) ----
  const [serverDate, setServerDate] = useState('');
  const [serverTime, setServerTime] = useState('');
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');

  // ---- initial-selection restoration bookkeeping ----
  const [initialSelection, setInitialSelection] = useState(null);
  const [selectionRestored, setSelectionRestored] = useState(false);

  // ---- in-progress flow snapshot (P2) ----
  // Persists the customer's WIP booking selection (location, date, time,
  // duration, courts, special requests) to sessionStorage so a 401-induced
  // re-login or accidental tab refresh doesn't wipe out their selection.
  // Cleared on successful submission and on logout.
  const FLOW_SNAPSHOT_KEY = 'smashzone_flow_snapshot';
  const FLOW_SNAPSHOT_MAX_AGE_MIN = 60;

  // ==================== DERIVED VALUES ====================
  const timeSlots = useMemo(
    () => buildTimeSlots(selectedLocation, selectedDate),
    [selectedLocation, selectedDate],
  );

  const isLocationClosed = useMemo(() => {
    const hours = getOperatingHoursForDate(selectedLocation, selectedDate);
    return hours?.closed === true;
  }, [selectedLocation, selectedDate]);

  const breakInfo = useMemo(() => {
    const hours = getOperatingHoursForDate(selectedLocation, selectedDate);
    if (!hours || !hours.breaks || hours.breaks.length === 0) return null;
    return hours.breaks;
  }, [selectedLocation, selectedDate]);

  const operatingHoursDisplay = useMemo(() => {
    const hours = getOperatingHoursForDate(selectedLocation, selectedDate);
    return hours ? formatOperatingHoursDisplay(hours) : null;
  }, [selectedLocation, selectedDate]);

  const availableSports = useMemo(() => {
    if (!selectedLocation?.sports) return ['badminton', 'pickleball'];
    return selectedLocation.sports;
  }, [selectedLocation]);

  // ==================== API LOADERS ====================
  const loadLocations = useCallback(async () => {
    try {
      const locs = await locationsAPI.getLocations();
      setLocations(locs);
    } catch (_error) {
      toast.error('Failed to load locations');
    }
  }, []);

  const loadServerTime = useCallback(async (hasRestoredSelection) => {
    try {
      const timeData = await getServerTime();
      setServerDate(timeData.date);
      setServerTime(timeData.time.substring(0, 5));
      setMinDate(timeData.date);

      if (!hasRestoredSelection && !selectedDate) {
        setSelectedDate(timeData.date);
      }

      const maxDateObj = new Date(timeData.date);
      maxDateObj.setMonth(maxDateObj.getMonth() + 1);
      setMaxDate(maxDateObj.toISOString().split('T')[0]);
    } catch (_error) {
      // Server clock unavailable — fall back to Brunei tz on the client,
      // never browser-local. Customers in other timezones still see the
      // correct calendar bounds.
      const today = bruneiToday();
      if (!hasRestoredSelection && !selectedDate) {
        setSelectedDate(today);
      }
      setMinDate(today);
      setMaxDate(bruneiDateOffset(30));
    }
    // selectedDate is intentionally omitted: we only set defaults when it's
    // empty, and re-running this when the date changes would clobber input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAvailability = useCallback(async (date, timeSlot) => {
    if (!selectedLocation || !date || !timeSlot) return;
    try {
      const avail = await bookingsAPI.checkAvailability(
        selectedLocation.id, date, timeSlot, duration,
      );
      setAvailability(avail);
    } catch (_error) {
      toast.error('Failed to check availability');
    }
  }, [selectedLocation, duration]);

  // Per-slot availability map for the WHOLE grid (mirrors the public Check
  // Availability page's pre-fetch-and-colour-code approach), so Step 1 can
  // show "Fully Booked"/"Limited"/"Available" on every slot before the
  // customer clicks one. Duration-aware: re-fetched whenever the customer
  // changes the session length, since a slot that's fine for 1H may be
  // fully booked for 2H/3H.
  const [slotAvailabilityMap, setSlotAvailabilityMap] = useState({});
  const loadSlotAvailabilityMap = useCallback(async () => {
    if (!selectedLocation || !selectedDate || timeSlots.length === 0) return;
    try {
      const entries = await Promise.all(timeSlots.map(async (slot) => {
        try {
          const avail = await bookingsAPI.checkAvailability(
            selectedLocation.id, selectedDate, slot, duration,
          );
          return [slot, avail];
        } catch (_error) {
          return [slot, null];
        }
      }));
      setSlotAvailabilityMap(Object.fromEntries(entries));
    } catch (error) {
      console.error('Error loading slot availability map:', error);
    }
  }, [selectedLocation, selectedDate, timeSlots, duration]);

  const loadSlotPricing = useCallback(async () => {
    if (!selectedLocation || !selectedDate || timeSlots.length === 0) return;
    try {
      const pricingData = {};
      await Promise.all(timeSlots.map(async (slot) => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/pricing/calculate?` +
            `location_id=${selectedLocation.id}&date=${selectedDate}` +
            `&time_slot=${slot}&sport=badminton&duration=1`,
          );
          pricingData[slot] = await response.json();
        } catch (_error) {
          pricingData[slot] = { price_per_hour: 14 };
        }
      }));
      setSlotPricing(pricingData);
    } catch (error) {
      console.error('Error loading slot pricing:', error);
    }
  }, [selectedLocation, selectedDate, timeSlots]);

  const loadPricingInfo = useCallback(async () => {
    if (!selectedLocation || !selectedDate || !selectedTimeSlot) return;
    try {
      const pricingData = await Promise.all(
        courtsRequested.map(async (court) => {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/pricing/calculate?` +
            `location_id=${selectedLocation.id}&date=${selectedDate}` +
            `&time_slot=${selectedTimeSlot}&sport=${court.sport}&duration=${duration}`,
          );
          return response.json();
        }),
      );
      setCourtsPricing(pricingData);
    } catch (error) {
      console.error('Error loading pricing:', error);
    }
  }, [selectedLocation, selectedDate, selectedTimeSlot, courtsRequested, duration]);

  // Loads the per-sport hourly rate for the currently-selected slot.
  // Used by Step 2 so the sport pill toggle can display every sport's price,
  // not only the currently-selected one. Re-fetched whenever the slot or
  // duration changes (one tiny call per available sport — same endpoint as
  // courtsPricing).
  const loadSportPriceMap = useCallback(async () => {
    if (!selectedLocation || !selectedDate || !selectedTimeSlot) return;
    if (!availableSports || availableSports.length === 0) return;
    try {
      const entries = await Promise.all(
        availableSports.map(async (sport) => {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/pricing/calculate?` +
            `location_id=${selectedLocation.id}&date=${selectedDate}` +
            `&time_slot=${selectedTimeSlot}&sport=${sport}&duration=${duration}`,
          );
          const data = await response.json();
          return [sport, data?.price_per_hour ?? null];
        }),
      );
      setSportPriceMap(Object.fromEntries(entries));
    } catch (error) {
      console.error('Error loading sport price map:', error);
    }
  }, [selectedLocation, selectedDate, selectedTimeSlot, availableSports, duration]);

  // ==================== EFFECTS ====================
  // Initial mount: restore any saved intent/selection, then load.
  // Auth gate lives in <CustomerProtectedRoute redirectTo="/auth" /> (see
  // App.js) — by the time this hook runs, `user` is guaranteed non-null.
  useEffect(() => {
    let hasRestoredSelection = false;

    // 1) bookingIntent from landing page clicks
    const bookingIntent = localStorage.getItem('bookingIntent');
    if (bookingIntent) {
      try {
        const intent = JSON.parse(bookingIntent);
        if (intent.date) setSelectedDate(intent.date);
        if (intent.timeSlot) setSelectedTimeSlot(intent.timeSlot);
        setInitialSelection({ type: 'intent', data: intent });
        hasRestoredSelection = true;

        // Consume the intent NOW, not later. Two bugs this prevents:
        //  (a) Tab close / hard refresh before the "apply preselected
        //      location" effect runs would leave the intent in storage,
        //      so the next visit to /book restores stale data days later.
        //  (b) If `intent.locationId` doesn't match any active location,
        //      the late clear at line ~330 was guarded by "location found"
        //      and would silently leak the intent forever.
        localStorage.removeItem('bookingIntent');

        // Sonner `id` dedupes if the same restore effect runs twice
        // (React.StrictMode in dev double-invokes effects on purpose to
        // surface non-idempotent side effects — without an id the customer
        // sees two stacked "Restored…" toasts on /book in dev/preview).
        if (intent.action === 'joinWaitlist') {
          toast.success(`Selection restored! Joining waitlist for ${intent.timeSlot}`,
            { id: 'flow-restore', duration: 5000 });
        } else {
          toast.info(`Restored: ${intent.locationName || 'Location'}, ${intent.date}`,
            { id: 'flow-restore' });
        }
      } catch (e) {
        console.error('Error parsing booking intent:', e);
        // Bad JSON — drop the poison value so we don't loop on it.
        localStorage.removeItem('bookingIntent');
      }
    }

    // 2) Navigation state (from a different page)
    if (!hasRestoredSelection && routeLocation.state?.preselected) {
      const { date, timeSlot } = routeLocation.state.preselected;
      if (date) setSelectedDate(date);
      if (timeSlot) setSelectedTimeSlot(timeSlot);
      setInitialSelection({ type: 'navigation', data: routeLocation.state.preselected });
      hasRestoredSelection = true;
    }

    // 3) Saved session selection from Check Availability page
    if (!hasRestoredSelection) {
      // 3a) WIP flow snapshot (P2) — survives 401-induced re-login.
      // Restored before the older smashzone_selection so a customer mid-flow
      // gets back to where they were (with duration + courts intact).
      try {
        const snapRaw = sessionStorage.getItem(FLOW_SNAPSHOT_KEY);
        if (snapRaw) {
          const snap = JSON.parse(snapRaw);
          const ageMin = (Date.now() - (snap.savedAt || 0)) / 60000;
          if (ageMin < FLOW_SNAPSHOT_MAX_AGE_MIN) {
            if (snap.date) setSelectedDate(snap.date);
            if (snap.timeSlot) setSelectedTimeSlot(snap.timeSlot);
            if (snap.duration) setDuration(snap.duration);
            if (Array.isArray(snap.courtsRequested) && snap.courtsRequested.length) {
              setCourtsRequested(snap.courtsRequested);
            }
            if (typeof snap.specialRequests === 'string') {
              setSpecialRequests(snap.specialRequests);
            }
            if (typeof snap.step === 'number' && snap.step >= 1 && snap.step <= 3) {
              setStep(snap.step);
            }
            setInitialSelection({ type: 'session', data: snap });
            hasRestoredSelection = true;
            toast.info('Restored your booking selection', { id: 'flow-restore' });
          } else {
            sessionStorage.removeItem(FLOW_SNAPSHOT_KEY);
          }
        }
      } catch (e) {
        console.error('Error parsing flow snapshot:', e);
        sessionStorage.removeItem(FLOW_SNAPSHOT_KEY);
      }
    }

    // 4) Older simple smashzone_selection from the Check Availability page
    if (!hasRestoredSelection) {
      const savedSelection = localStorage.getItem('smashzone_selection');
      if (savedSelection) {
        try {
          const selection = JSON.parse(savedSelection);
          const savedAt = new Date(selection.savedAt);
          const hoursDiff = (new Date() - savedAt) / (1000 * 60 * 60);
          if (hoursDiff < 24) {
            if (selection.date) setSelectedDate(selection.date);
            if (selection.timeSlot) setSelectedTimeSlot(selection.timeSlot);
            setInitialSelection({ type: 'session', data: selection });
            hasRestoredSelection = true;
            // Consume immediately — same reasoning as bookingIntent above.
            localStorage.removeItem('smashzone_selection');
            toast.info(`Restored your previous selection: ${selection.locationName || 'Location'}, ${selection.date}`,
              { id: 'flow-restore' });
          }
        } catch (e) {
          console.error('Error parsing saved selection:', e);
          localStorage.removeItem('smashzone_selection');
        }
      }
    }

    setSelectionRestored(hasRestoredSelection);
    loadServerTime(hasRestoredSelection);
    loadLocations();
    // Deliberately only triggers on user change — restoration should
    // happen exactly once per session. Re-running on every locals change
    // would overwrite user input mid-flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Apply preselected location once locations are loaded.
  useEffect(() => {
    if (locations.length === 0) return;
    if (initialSelection) {
      const locationId = initialSelection.data.locationId;
      if (locationId) {
        const location = locations.find((l) => l.id === locationId);
        if (location) {
          setSelectedLocation(location);
          // Note: bookingIntent + smashzone_selection are already consumed
          // up in the restoration effect above (Tier A fix). Nothing to
          // remove here.
          return;
        }
      }
    }
    if (!selectedLocation) setSelectedLocation(locations[0]);
    // selectedLocation intentionally omitted — we only want to auto-pick
    // "first location" once when no restoration happened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, initialSelection]);

  useEffect(() => {
    if (selectedLocation && selectedDate && timeSlots.length > 0) {
      loadSlotPricing();
      loadSlotAvailabilityMap();
    }
  }, [selectedLocation, selectedDate, timeSlots, duration, loadSlotPricing, loadSlotAvailabilityMap]);

  useEffect(() => {
    if (selectedDate && selectedTimeSlot && selectedLocation) {
      checkAvailability(selectedDate, selectedTimeSlot);
      loadPricingInfo();
      loadSportPriceMap();
    }
  }, [selectedDate, selectedTimeSlot, selectedLocation, courtsRequested, duration,
      checkAvailability, loadPricingInfo, loadSportPriceMap]);

  // P2: snapshot the in-progress flow to sessionStorage so a 401 / re-login
  // / accidental refresh doesn't wipe the customer's selection.
  // Only writes once the user has actually started selecting (location +
  // date) — avoids polluting sessionStorage on first mount.
  useEffect(() => {
    if (!user) return;
    if (!selectedLocation || !selectedDate) return;
    try {
      const snap = {
        locationId: selectedLocation?.id,
        locationName: selectedLocation?.name,
        date: selectedDate,
        timeSlot: selectedTimeSlot,
        duration,
        courtsRequested,
        specialRequests,
        step,
        savedAt: Date.now(),
      };
      sessionStorage.setItem(FLOW_SNAPSHOT_KEY, JSON.stringify(snap));
    } catch (e) {
      // Storage full / disallowed (incognito, quota exceeded). The snapshot
      // is a comfort feature — log so a real bug surfaces in DevTools but
      // don't break the booking flow.
      console.warn('Could not save booking-flow snapshot:', e?.message || e);
    }
  }, [user, selectedLocation, selectedDate, selectedTimeSlot, duration,
      courtsRequested, specialRequests, step]);

  // ==================== HANDLERS ====================
  const handleLocationChange = useCallback((locationId) => {
    const location = locations.find((l) => l.id === locationId);
    setSelectedLocation(location);
    setSelectedTimeSlot('');
    setAvailability(null);
    setSlotPricing({});
    setSlotAvailabilityMap({});
  }, [locations]);

  const getConsecutiveSlots = useCallback(
    (startSlot, dur) => computeConsecutiveSlots(timeSlots, startSlot, dur),
    [timeSlots],
  );

  const isTimeSlotPast = useCallback(
    (slot) => computeIsTimeSlotPast(slot, serverDate, serverTime, selectedDate),
    [serverDate, serverTime, selectedDate],
  );

  const checkConsecutiveSlotsAvailability = useCallback(async (startSlot, dur) => {
    if (!selectedLocation || !selectedDate || dur === 1) return true;
    const consecutive = computeConsecutiveSlots(timeSlots, startSlot, dur);
    if (consecutive.length < dur) {
      toast.error(`Not enough consecutive time slots available after ${startSlot}`);
      return false;
    }
    try {
      for (const slot of consecutive) {
        const avail = await bookingsAPI.checkAvailability(
          selectedLocation.id, selectedDate, slot,
        );
        if (avail.available_courts === 0) {
          toast.error(`Time slot ${slot} is fully booked. Please select a different starting time.`);
          return false;
        }
      }
      return true;
    } catch (error) {
      console.error('Error checking consecutive availability:', error);
      return false;
    }
  }, [selectedLocation, selectedDate, timeSlots]);

  const handleTimeSlotSelection = useCallback(async (slot) => {
    if (duration > 1) {
      const ok = await checkConsecutiveSlotsAvailability(slot, duration);
      if (ok) {
        setSelectedTimeSlot(slot);
        const consecutive = computeConsecutiveSlots(timeSlots, slot, duration);
        toast.success(`Selected ${duration}-hour session: ${consecutive.join(', ')}`);
      }
    } else {
      setSelectedTimeSlot(slot);
    }
  }, [duration, timeSlots, checkConsecutiveSlotsAvailability]);

  const addCourt = useCallback(() => {
    setCourtsRequested((prev) => {
      if (prev.length >= 5) return prev;
      return [
        ...prev,
        {
          sport: 'badminton',
          quantity: 1,
          _uid: `court-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        },
      ];
    });
  }, []);

  const removeCourt = useCallback((index) => {
    setCourtsRequested((prev) => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);
  }, []);

  const updateCourtSport = useCallback((index, sport) => {
    setCourtsRequested((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], sport };
      return updated;
    });
  }, []);

  // "Next" on step 1 — either push to step 2 OR, if the slot is full, send
  // the customer straight to the waitlist (legacy behaviour preserved).
  const handleDateTimeNext = useCallback(async () => {
    if (!selectedDate || !selectedTimeSlot) {
      toast.error('Please select date and time');
      return;
    }

    if (availability?.is_fully_booked) {
      setJoiningWaitlist(true);
      try {
        const waitlistData = {
          customer_name: user?.name || 'Guest',
          customer_phone: user?.phone || '',
          customer_email: user?.email || '',
          is_guest: isGuest,
          user_id: isGuest ? null : user?.id,
          location_id: selectedLocation.id,
          preferred_date: selectedDate,
          preferred_time_slot: selectedTimeSlot,
          sport: 'badminton',
          courts_needed: 1,
          duration,
        };
        await bookingsAPI.joinWaitlist(waitlistData);

        localStorage.removeItem('smashzone_selection');
        localStorage.removeItem('bookingIntent');
        sessionStorage.removeItem(FLOW_SNAPSHOT_KEY);

        toast.success(
          `Successfully joined waitlist! You are #${(availability.waitlist_count || 0) + 1} in queue.`,
          { duration: 6000, description: "You'll be notified if a court becomes available." },
        );
        navigate('/home');
      } catch (_error) {
        toast.error('Failed to join waitlist');
      } finally {
        setJoiningWaitlist(false);
      }
      return;
    }

    setStep(2);
  }, [selectedDate, selectedTimeSlot, availability, selectedLocation, user, isGuest, duration, navigate]);

  const handleSubmitBooking = useCallback(async () => {
    if (!selectedLocation) { toast.error('Please select a location'); return; }
    if (!selectedDate || !selectedTimeSlot) {
      toast.error('Please select date and time');
      return;
    }
    if (availability?.is_fully_booked) {
      setShowWaitlistDialog(true);
      return;
    }

    setLoading(true);
    try {
      const bookingData = {
        user_id: isGuest ? null : user?.id,
        guest_phone: isGuest ? user?.phone : null,
        location_id: selectedLocation.id,
        booking_date: selectedDate,
        time_slot: selectedTimeSlot,
        duration,
        courts_requested: courtsRequested,
        special_requests: specialRequests,
      };
      const booking = await bookingsAPI.createBooking(bookingData);

      localStorage.removeItem('smashzone_selection');
      localStorage.removeItem('bookingIntent');
      sessionStorage.removeItem(FLOW_SNAPSHOT_KEY);

      toast.success('Booking request submitted!');
      navigate(`/booking-submitted/${booking.id}`);
    } catch (_error) {
      toast.error('Failed to submit booking');
    } finally {
      setLoading(false);
    }
  }, [selectedLocation, selectedDate, selectedTimeSlot, availability, isGuest, user,
      duration, courtsRequested, specialRequests, navigate]);

  const handleJoinWaitlist = useCallback(async () => {
    setJoiningWaitlist(true);
    try {
      const waitlistData = {
        customer_name: user?.name || 'Guest',
        customer_phone: user?.phone || '',
        customer_email: user?.email || '',
        is_guest: isGuest,
        user_id: isGuest ? null : user?.id,
        location_id: selectedLocation.id,
        preferred_date: selectedDate,
        preferred_time_slot: selectedTimeSlot,
        sport: courtsRequested[0]?.sport || 'badminton',
        courts_needed: courtsRequested.reduce((sum, c) => sum + c.quantity, 0),
        duration,
      };
      await bookingsAPI.joinWaitlist(waitlistData);

      // Clear ALL three restore keys — same as the other waitlist exit
      // (handleProceedToCourts). Previously this path forgot
      // FLOW_SNAPSHOT_KEY, so opening /book again after joining via the
      // dialog would "Restore your booking selection" with the slot the
      // customer just joined the waitlist for — confusing.
      localStorage.removeItem('smashzone_selection');
      localStorage.removeItem('bookingIntent');
      sessionStorage.removeItem(FLOW_SNAPSHOT_KEY);

      setShowWaitlistDialog(false);
      toast.success('Successfully joined waitlist! You will be notified if a court becomes available.');
      navigate('/home');
    } catch (_error) {
      toast.error('Failed to join waitlist');
    } finally {
      setJoiningWaitlist(false);
    }
  }, [user, isGuest, selectedLocation, selectedDate, selectedTimeSlot,
      courtsRequested, duration, navigate]);

  // Step navigation (back button + explicit step changes)
  const goBack = useCallback(() => {
    if (step === 1) navigate('/home');
    else setStep(step - 1);
  }, [step, navigate]);

  const goToStep = useCallback((s) => setStep(s), []);

  const handleDateChange = useCallback((value) => {
    setSelectedDate(value);
    setSelectedTimeSlot('');  // clear when date changes
  }, []);

  const handleDurationChange = useCallback((hours) => {
    setDuration(hours);
    setSelectedTimeSlot('');  // clear when duration changes
  }, []);

  const closeWaitlistDialog = useCallback(() => setShowWaitlistDialog(false), []);

  return {
    // identity
    user, isGuest,

    // step + flags
    step, loading, joiningWaitlist, showWaitlistDialog,

    // data
    locations, selectedLocation, selectedDate, selectedTimeSlot, duration,
    availability, slotPricing, slotAvailabilityMap, courtsRequested, courtsPricing, sportPriceMap,
    specialRequests,

    // derived
    timeSlots, isLocationClosed, breakInfo, operatingHoursDisplay, availableSports,

    // helpers
    isTimeSlotPast, getConsecutiveSlots,

    // time bounds
    minDate, maxDate,

    // actions
    goBack, goToStep,
    handleLocationChange,
    handleDateChange, handleDurationChange,
    handleTimeSlotSelection,
    handleDateTimeNext,
    addCourt, removeCourt, updateCourtSport,
    setSpecialRequests,
    handleSubmitBooking,
    handleJoinWaitlist,
    closeWaitlistDialog,

    // (exposed so the shell can call setSelectionRestored if ever needed; we
    // keep it here to avoid dead-code imports in the shell)
    _internal: { selectionRestored },
  };
}
